<script lang="ts">
  import { onDestroy } from 'svelte';
  import { liveEventApplicationsStore } from '../../lib/convexRealtime';
  import { decideEventApplication } from '../../lib/convexApi';
  import type { EventApplicationEntry } from '../../lib/types';
  import ResponseAnswers from './ResponseAnswers.svelte';
  import Notice from '../ui/Notice.svelte';
  import LoadingBlock from '../ui/LoadingBlock.svelte';

  export let eventId: string;
  export let eventName: string;

  let store: ReturnType<typeof liveEventApplicationsStore> | null = null;
  let unsubscribe: (() => void) | null = null;
  let subscribedId: string | null = null;

  let applications: EventApplicationEntry[] = [];
  let loading = true;
  let error: string | null = null;

  let pendingActionId: string | null = null;
  let actionError: string | null = null;

  $: if (eventId && eventId !== subscribedId) {
    subscribedId = eventId;
    unsubscribe?.();
    store?.dispose();
    store = liveEventApplicationsStore(eventId);
    unsubscribe = store.subscribe(state => {
      applications = state.data ?? [];
      loading = state.loading;
      error = state.error;
    });
  }

  onDestroy(() => {
    unsubscribe?.();
    store?.dispose();
  });

  $: pending = applications.filter(entry => entry.status === 'pending');
  $: decided = applications.filter(entry => entry.status !== 'pending');

  async function decide(entry: EventApplicationEntry, approve: boolean) {
    pendingActionId = entry.applicationId;
    actionError = null;
    try {
      await decideEventApplication(entry.applicationId, approve);
      await store?.refresh();
    } catch (err) {
      actionError = err instanceof Error ? err.message : 'Action failed. Please try again.';
    } finally {
      pendingActionId = null;
    }
  }
</script>

{#if loading && applications.length === 0}
  <LoadingBlock label="Loading applications…" />
{:else if error}
  <Notice variant="error">Could not load applications: {error}</Notice>
{:else if applications.length > 0}
  <section class="eap surface surface--sunken" aria-label={`Applications for ${eventName}`}>
    <h4>Ticket applications · {eventName}</h4>

    {#if actionError}
      <Notice variant="error">{actionError}</Notice>
    {/if}

    {#if pending.length > 0}
      <ul>
        {#each pending as entry (entry.applicationId)}
          <li class="eap__row eap__row--pending">
            <div class="eap__info">
              <span class="eap__name">{entry.person?.name ?? 'Unknown'}</span>
              {#if entry.person?.email}
                <span class="eap__meta">{entry.person.email}</span>
              {/if}
              {#if entry.invitedBy}
                <span class="eap__meta eap__provenance">
                  Invited by {entry.invitedBy.personName}{entry.invitedBy.eventName
                    ? ` · ${entry.invitedBy.eventName}`
                    : ''}
                </span>
              {/if}
              {#if entry.note}
                <blockquote class="eap__note">“{entry.note}”</blockquote>
              {/if}
              {#if entry.response}
                <details class="eap__answers">
                  <summary>Application answers</summary>
                  <ResponseAnswers response={entry.response} />
                </details>
              {/if}
            </div>
            <div class="eap__actions">
              <button
                type="button"
                class="accent"
                disabled={pendingActionId === entry.applicationId}
                on:click={() => decide(entry, true)}
              >
                Approve
              </button>
              <button
                type="button"
                class="ghost"
                disabled={pendingActionId === entry.applicationId}
                on:click={() => decide(entry, false)}
              >
                Reject
              </button>
            </div>
          </li>
        {/each}
      </ul>
    {:else}
      <p class="eap__empty">No open applications.</p>
    {/if}

    {#if decided.length > 0}
      <details class="eap__decided">
        <summary>Decided ({decided.length})</summary>
        <ul>
          {#each decided as entry (entry.applicationId)}
            <li class="eap__row">
              <div class="eap__info">
                <span class="eap__name">{entry.person?.name ?? 'Unknown'}</span>
                <span class="eap__meta">{entry.status}</span>
              </div>
            </li>
          {/each}
        </ul>
      </details>
    {/if}
  </section>
{/if}

<style>
  .eap {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-4);
    border-radius: var(--radius-lg);
  }

  .eap h4 {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .eap ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .eap__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    border: var(--border-subtle);
    background: var(--color-surface-850);
    flex-wrap: wrap;
  }

  .eap__row--pending {
    border-color: rgb(var(--accent-rgb) / 0.35);
  }

  .eap__info {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
  }

  .eap__name {
    font-weight: 600;
    font-size: var(--font-size-sm);
  }

  .eap__meta {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .eap__provenance {
    color: var(--color-text-amber);
  }

  .eap__note {
    margin: var(--space-1) 0 0;
    padding-left: var(--space-3);
    border-left: 2px solid rgb(var(--accent-rgb) / 0.35);
    font-size: var(--font-size-xs);
    color: var(--color-text-secondary);
    font-style: italic;
  }

  .eap__answers {
    margin-top: var(--space-1);
  }

  .eap__answers summary {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
    cursor: pointer;
  }

  .eap__answers[open] summary {
    margin-bottom: var(--space-2);
  }

  .eap__actions {
    display: flex;
    gap: var(--space-2);
    flex-shrink: 0;
  }

  .eap__actions button {
    font-size: var(--font-size-xs);
    padding: var(--space-2) var(--space-3);
  }

  .eap__empty {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
  }

  .eap__decided summary {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
    cursor: pointer;
  }

  .eap__decided[open] summary {
    margin-bottom: var(--space-2);
  }
</style>
